import axios, { cfAxios, sessionControlAxios } from '@/utils/init-axios.js'
import { session } from './session'
import { pinSessionProvider, usesFirebaseSession } from './session-config'
import { eventBus } from '@/plugins/eventbus.ts'
import { clearIntegrationTypeCookie } from './cookies'
import { MerchantNotFoundError } from '@/store/datastore/actions.js'

/**
 * Whether a merchant load failed because the id it was given does not name this user's merchant:
 * the store's typed rejection, or a 404 from Django's or core's read. Never the message text.
 */
function isStaleMerchantError(error) {
  return (
    error instanceof MerchantNotFoundError || error?.response?.status === 404
  )
}

function dropStaleMerchantId(error) {
  console.warn(
    'The stored merchant id is stale; loading the default merchant.',
    error
  )
  localStorage.removeItem('current_merchant_id')
}

/**
 * The sign-in merchant load: `getMerchant` with no id, run once more after dropping a stale stored
 * id. Any other failure, or a second one, rejects.
 */
export async function loadDefaultMerchant(store) {
  try {
    return await store.dispatch('getMerchant', { merchantID: null })
  } catch (error) {
    if (!isStaleMerchantError(error)) {
      throw error
    }

    dropStaleMerchantId(error)

    return store.dispatch('getMerchant', { merchantID: null })
  }
}

/**
 * Loads the profile and the current merchant for a session that is already signed in.
 *
 * A stale merchant id — from `localStorage.current_merchant_id` or the route — is dropped and the
 * merchant loaded once more with no id, which resolves the user's own. Any other failure, or a
 * second one, lands in `startupLoadError`, which App.vue renders in place of the router view.
 */
export async function loadStartup({ store, router }) {
  if (usesFirebaseSession()) return session.retryBootstrap()
  const merchantID = router.currentRoute.params?.merchantID

  try {
    const [profile, merchant] = await Promise.allSettled([
      store.dispatch('getProfile'),
      store.dispatch('getMerchant', { merchantID })
    ])

    if (profile.status === 'rejected') {
      throw profile.reason
    }

    if (merchant.status === 'rejected') {
      if (!isStaleMerchantError(merchant.reason)) {
        throw merchant.reason
      }

      dropStaleMerchantId(merchant.reason)

      if (merchantID) {
        // A guard redirect rejects the push; Home is still where the app ends up.
        await router.push({ name: 'Home' }).catch(() => {})
      }

      await store.dispatch('getMerchant', { merchantID: null })
    }

    if (!store.getters.isAuthenticated) {
      return
    }

    store.commit('setStartupLoadError', null)
    store.commit('setRefreshComplete', true)
  } catch (error) {
    const status = error?.response?.status ?? null

    console.error(
      `Could not load the merchant at start-up (${status ? `HTTP ${status}` : 'no HTTP status'}).`,
      error
    )
    // A 401 that ends in logout lands here after the user is already signed out.
    if (store.getters.isAuthenticated) {
      store.commit('setStartupLoadError', { status })
    }
  }
}

/**
 * The page-load entry point: refresh the access token, then {@link loadStartup}.
 *
 * A failed refresh is not a load failure. `refreshAccess` has already logged the user out and sent
 * them to the login page, so nothing is recorded for the error screen.
 */
export async function startSession({ store, router }) {
  if (usesFirebaseSession()) return startFirebaseSession({ store, router })
  if (!localStorage.getItem('blytzpay_refresh_token')) {
    return
  }

  let response

  try {
    response = await store.dispatch('refreshAccess')
  } catch {
    return
  }

  const data = response?.data

  if (!data) {
    return
  }

  axios.defaults.headers.common['Authorization'] = `Bearer ${data.access}`

  await loadStartup({ store, router })
}

let firebaseStart
let firebaseStartupRevision = 0
let cancelFirebaseStart

function invalidateFirebaseStartup() {
  ++firebaseStartupRevision
  cancelFirebaseStart?.()
  cancelFirebaseStart = null
}

export function startFirebaseSession(
  { store, router },
  providerFactory,
  options = {}
) {
  if (firebaseStart) return firebaseStart
  const revision = ++firebaseStartupRevision
  session.configure({
    publish: value => store.commit('setSession', value),
    activity: (at, inactive) =>
      store.commit('setSessionActivity', { at, inactive }),
    clear: () => {
      eventBus.emit('sessionCleared', {})
      eventBus.emit('clearInterval', {})
      clearInterval(store.state.authStore.heartbeat)
      store.commit('setHeartbeat', null)
      store.commit('resetState')
      store.commit('clearSessionData')
      store.commit('resetUserPreferences')
      localStorage.removeItem('current_merchant_id')
      clearIntegrationTypeCookie()
      delete axios.defaults.headers.common.Authorization
    },
    readAuthorization: async (path, bearer) => {
      const response = await cfAxios.get(path, {
        headers: { Authorization: `Bearer ${bearer}` }
      })

      return response.data
    },
    revoke: (path, bearer) =>
      sessionControlAxios.post(path, null, {
        headers: { Authorization: `Bearer ${bearer}` }
      }),
    bootstrap: async generation => {
      await store.dispatch('getProfile')
      session.assertCurrent(generation)
      await loadDefaultMerchant(store)
      session.assertCurrent(generation)
      store.commit('setStartupLoadError', null)
      store.commit('setRefreshComplete', true)
      await store.dispatch('heartbeat')
    },
    error: error =>
      store.commit('setStartupLoadError', {
        status: error?.response?.status ?? null
      }),
    revocationFailed: () => eventBus.emit('sessionRevocationFailed', {}),
    signedOut: () => {
      invalidateFirebaseStartup()
      if (router.currentRoute.name !== 'Login')
        void router.push({ name: 'Login' }).catch(() => {})
    }
  })
  const work = (async () => {
    const provider = providerFactory
      ? await providerFactory()
      : (await import('./firebase-session')).merchantProvider()
    if (revision !== firebaseStartupRevision) return
    await session.start(provider, options)
  })().catch(error => {
    if (revision !== firebaseStartupRevision) return
    firebaseStart = null
    store.commit('setStartupLoadError', {
      status: error?.response?.status ?? null
    })
    throw error
  })

  firebaseStart = Promise.race([
    work,
    new Promise(resolve => {
      cancelFirebaseStart = resolve
    })
  ])

  return firebaseStart
}

export function beginFirebaseSession(context, providerFactory) {
  if (session.isClosing) throw new Error('Wait for sign-out to finish')
  if (session.status === 'ready')
    throw new Error('Sign out before starting another Firebase entry')
  invalidateFirebaseStartup()
  session.dispose()
  firebaseStart = null
  pinSessionProvider('firebase')

  return startFirebaseSession(context, providerFactory, { freshEntry: true })
}
